import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useInvoice } from '../context/InvoiceContext';

const { FiSave, FiRefreshCw, FiBuilding, FiHash, FiUpload, FiX, FiDownload, FiUploadCloud, FiGlobe, FiDroplet, FiCreditCard } = FiIcons;

const Settings = () => {
  const { settings, updateSettings, exportData, importData } = useInvoice();
  const [activeProfile, setActiveProfile] = useState('default');
  const [formData, setFormData] = useState({
    invoiceNumber: settings.invoiceNumber,
    travelInvoiceNumber: settings.travelInvoiceNumber || 1,
    marketingInvoiceNumber: settings.marketingInvoiceNumber || 1,
    excursionBookingNumber: settings.excursionBookingNumber || 1,
    companyInfo: { ...settings.companyInfo },
    logoUrl: settings.logoUrl || '',
    travelAgencyInfo: {
      name: '',
      email: '',
      phone: '',
      logoUrl: '',
      ...(settings.travelAgencyInfo || {})
    },
    colors: settings.colors || {
      primary: '#1e3a8a',
      secondary: '#6366f1',
      accent: '#ec4899',
      dark: '#0f172a',
      light: '#f8fafc'
    },
    stripePublishableKey: settings.stripePublishableKey || ''
  });
  const [logoPreview, setLogoPreview] = useState(settings.logoUrl || '');
  const [travelLogoPreview, setTravelLogoPreview] = useState(settings.travelAgencyInfo?.logoUrl || '');
  const [backupStatus, setBackupStatus] = useState(null);
  const fileInputRef = useRef(null);
  const travelLogoInputRef = useRef(null);

  const handleCompanyInfoChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      companyInfo: { ...prev.companyInfo, [field]: value }
    }));
  };

  const handleTravelAgencyInfoChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      travelAgencyInfo: { ...prev.travelAgencyInfo, [field]: value }
    }));
  };

  const handleColorChange = (colorKey, value) => {
    setFormData(prev => ({
      ...prev,
      colors: { ...prev.colors, [colorKey]: value }
    }));
  };

  const resetColors = () => {
    if (window.confirm('Are you sure you want to reset colors to default?')) {
      setFormData(prev => ({
        ...prev,
        colors: {
          primary: '#1e3a8a',
          secondary: '#6366f1',
          accent: '#ec4899',
          dark: '#0f172a',
          light: '#f8fafc'
        }
      }));
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert('Logo file size should be less than 2MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        const logoUrl = event.target.result;
        setLogoPreview(logoUrl);
        setFormData(prev => ({ ...prev, logoUrl }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleTravelLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert('Logo file size should be less than 2MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        const logoUrl = event.target.result;
        setTravelLogoPreview(logoUrl);
        setFormData(prev => ({
          ...prev,
          travelAgencyInfo: { ...prev.travelAgencyInfo, logoUrl }
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setLogoPreview('');
    setFormData(prev => ({ ...prev, logoUrl: '' }));
  };

  const removeTravelLogo = () => {
    setTravelLogoPreview('');
    setFormData(prev => ({
      ...prev,
      travelAgencyInfo: { ...prev.travelAgencyInfo, logoUrl: '' }
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    updateSettings(formData);
    alert('Settings saved successfully!');
  };

  const resetInvoiceNumber = (type) => {
    const typeNames = {
      default: 'default invoice',
      travel: 'travel invoice',
      marketing: 'marketing invoice',
      excursion: 'excursion booking'
    };
    
    if (window.confirm(`Are you sure you want to reset the ${typeNames[type]} number to 1?`)) {
      switch (type) {
        case 'travel':
          setFormData(prev => ({ ...prev, travelInvoiceNumber: 1 }));
          break;
        case 'marketing':
          setFormData(prev => ({ ...prev, marketingInvoiceNumber: 1 }));
          break;
        case 'excursion':
          setFormData(prev => ({ ...prev, excursionBookingNumber: 1 }));
          break;
        default:
          setFormData(prev => ({ ...prev, invoiceNumber: 1 }));
          break;
      }
    }
  };

  const handleExportData = () => {
    const success = exportData();
    if (success) {
      setBackupStatus({
        type: 'success',
        message: 'Data exported successfully! Your backup file has been downloaded.'
      });
      setTimeout(() => setBackupStatus(null), 5000);
    } else {
      setBackupStatus({
        type: 'error',
        message: 'Failed to export data. Please try again.'
      });
    }
  };

  const handleImportClick = () => {
    fileInputRef.current.click();
  };

  const handleImportData = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const success = importData(event.target.result);
        if (success) {
          setBackupStatus({
            type: 'success',
            message: 'Data imported successfully! Your app has been updated with the imported data.'
          });
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        } else {
          setBackupStatus({
            type: 'error',
            message: 'Failed to import data. The file may be corrupted or in an invalid format.'
          });
        }
      } catch (error) {
        setBackupStatus({
          type: 'error',
          message: 'Error reading the import file. Please ensure it is a valid backup file.'
        });
      }
    };
    reader.readAsText(file);
    e.target.value = null;
  };

  const colorOptions = [
    { key: 'primary', label: 'Primary Color', description: 'Main header and table header' },
    { key: 'secondary', label: 'Secondary Color', description: 'Card headers and borders' },
    { key: 'accent', label: 'Accent Color', description: 'Highlights and important elements' },
    { key: 'dark', label: 'Dark Color', description: 'Text and headings' },
    { key: 'light', label: 'Light Color', description: 'Backgrounds and subtle elements' }
  ];

  return (
    <div className="max-w-4xl mx-auto p-6">
      <motion.div
        className="bg-white rounded-xl shadow-lg overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Settings</h1>
            <p className="text-gray-600">Manage your invoice settings and business information</p>
          </div>

          {/* Profile Tabs */}
          <div className="mb-8">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setActiveProfile('default')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeProfile === 'default'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center">
                    <SafeIcon icon={FiBuilding} className="w-4 h-4 mr-2" />
                    Default Business
                  </div>
                </button>
                <button
                  onClick={() => setActiveProfile('travel')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeProfile === 'travel'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center">
                    <SafeIcon icon={FiGlobe} className="w-4 h-4 mr-2" />
                    Travel Agency
                  </div>
                </button>
                <button
                  onClick={() => setActiveProfile('colors')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeProfile === 'colors'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center">
                    <SafeIcon icon={FiDroplet} className="w-4 h-4 mr-2" />
                    Colors
                  </div>
                </button>
              </nav>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Payment Gateway Settings */}
            <div>
              <div className="flex items-center mb-6">
                <SafeIcon icon={FiCreditCard} className="w-6 h-6 text-gray-400 mr-3" />
                <h2 className="text-xl font-semibold text-gray-900">Payment Gateway</h2>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Stripe Publishable Key
                  </label>
                  <input
                    type="text"
                    value={formData.stripePublishableKey}
                    onChange={(e) => setFormData(prev => ({ ...prev, stripePublishableKey: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="pk_live_..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This key is safe to use in the browser. You can find it in your Stripe dashboard.
                  </p>
                </div>
              </div>
            </div>

            {/* Invoice Settings */}
            <div>
              <div className="flex items-center mb-6">
                <SafeIcon icon={FiHash} className="w-6 h-6 text-gray-400 mr-3" />
                <h2 className="text-xl font-semibold text-gray-900">Invoice Numbering</h2>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg space-y-6">
                <p className="text-sm text-gray-600 mb-4">
                  Each invoice type has its own numbering sequence with a unique prefix.
                </p>
                
                {/* Default Invoice */}
                <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
                  <div className="flex-1 mr-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Default Invoice Number <span className="text-blue-600 font-mono">(INV-)</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.invoiceNumber}
                      onChange={(e) => setFormData(prev => ({ ...prev, invoiceNumber: parseInt(e.target.value) || 1 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Next invoice: <span className="font-mono font-semibold">INV-{formData.invoiceNumber}</span>
                    </p>
                  </div>
                  <motion.button
                    type="button"
                    onClick={() => resetInvoiceNumber('default')}
                    className="flex items-center px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2" />
                    Reset
                  </motion.button>
                </div>

                {/* Travel Invoice */}
                <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
                  <div className="flex-1 mr-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Travel Invoice Number <span className="text-cyan-600 font-mono">(TRV-)</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.travelInvoiceNumber}
                      onChange={(e) => setFormData(prev => ({ ...prev, travelInvoiceNumber: parseInt(e.target.value) || 1 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Next invoice: <span className="font-mono font-semibold">TRV-{formData.travelInvoiceNumber}</span>
                    </p>
                  </div>
                  <motion.button
                    type="button"
                    onClick={() => resetInvoiceNumber('travel')}
                    className="flex items-center px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2" />
                    Reset
                  </motion.button>
                </div>

                {/* Marketing Invoice */}
                <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
                  <div className="flex-1 mr-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Marketing Invoice Number <span className="text-purple-600 font-mono">(MKT-)</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.marketingInvoiceNumber}
                      onChange={(e) => setFormData(prev => ({ ...prev, marketingInvoiceNumber: parseInt(e.target.value) || 1 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Next invoice: <span className="font-mono font-semibold">MKT-{formData.marketingInvoiceNumber}</span>
                    </p>
                  </div>
                  <motion.button
                    type="button"
                    onClick={() => resetInvoiceNumber('marketing')}
                    className="flex items-center px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2" />
                    Reset
                  </motion.button>
                </div>

                {/* Excursion Booking */}
                <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
                  <div className="flex-1 mr-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Excursion Booking Number <span className="text-orange-600 font-mono">(EXC-)</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.excursionBookingNumber}
                      onChange={(e) => setFormData(prev => ({ ...prev, excursionBookingNumber: parseInt(e.target.value) || 1 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Next booking: <span className="font-mono font-semibold">EXC-{formData.excursionBookingNumber}</span>
                    </p>
                  </div>
                  <motion.button
                    type="button"
                    onClick={() => resetInvoiceNumber('excursion')}
                    className="flex items-center px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2" />
                    Reset
                  </motion.button>
                </div>
              </div>
            </div>

            {/* Data Backup & Restore */}
            <div>
              <div className="flex items-center mb-6">
                <SafeIcon icon={FiDownload} className="w-6 h-6 text-gray-400 mr-3" />
                <h2 className="text-xl font-semibold text-gray-900">Data Backup & Restore</h2>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-sm text-gray-600 mb-4">
                  Backup your invoice data to prevent loss during app updates or browser clearing. You can restore your data from a backup file at any time.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <motion.button
                    type="button"
                    onClick={handleExportData}
                    className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <SafeIcon icon={FiDownload} className="w-4 h-4 mr-2" />
                    Export Data Backup
                  </motion.button>
                  
                  <motion.button
                    type="button"
                    onClick={handleImportClick}
                    className="flex items-center justify-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <SafeIcon icon={FiUploadCloud} className="w-4 h-4 mr-2" />
                    Import From Backup
                  </motion.button>
                  
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImportData}
                    accept=".json"
                    className="hidden"
                  />
                </div>
                
                {backupStatus && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`mt-4 p-3 rounded-lg ${
                      backupStatus.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {backupStatus.message}
                  </motion.div>
                )}
              </div>
            </div>

            {/* Color Customization */}
            {activeProfile === 'colors' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center">
                    <SafeIcon icon={FiDroplet} className="w-6 h-6 text-gray-400 mr-3" />
                    <h2 className="text-xl font-semibold text-gray-900">Invoice Colors</h2>
                  </div>
                  <motion.button
                    type="button"
                    onClick={resetColors}
                    className="flex items-center px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2" />
                    Reset Colors
                  </motion.button>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg space-y-6">
                  <p className="text-sm text-gray-600 mb-4">
                    Customize the colors used in your travel invoices. These colors will be applied to headers, cards, and highlights.
                  </p>

                  {colorOptions.map((option) => (
                    <div key={option.key} className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-900 mb-1">
                          {option.label}
                        </label>
                        <p className="text-xs text-gray-500">{option.description}</p>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div 
                          className="w-12 h-12 rounded-lg border-2 border-gray-300 shadow-sm"
                          style={{ backgroundColor: formData.colors[option.key] }}
                        />
                        <input
                          type="color"
                          value={formData.colors[option.key]}
                          onChange={(e) => handleColorChange(option.key, e.target.value)}
                          className="w-16 h-12 rounded-lg cursor-pointer border border-gray-300"
                        />
                        <input
                          type="text"
                          value={formData.colors[option.key]}
                          onChange={(e) => handleColorChange(option.key, e.target.value)}
                          className="w-28 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                          placeholder="#000000"
                        />
                      </div>
                    </div>
                  ))}

                  {/* Color Preview */}
                  <div className="mt-6 p-6 bg-white rounded-lg border border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-900 mb-4">Color Preview</h3>
                    <div className="grid grid-cols-5 gap-4">
                      {colorOptions.map((option) => (
                        <div key={option.key} className="text-center">
                          <div 
                            className="w-full h-20 rounded-lg mb-2 shadow-sm"
                            style={{ backgroundColor: formData.colors[option.key] }}
                          />
                          <p className="text-xs font-medium text-gray-700">{option.label}</p>
                          <p className="text-xs text-gray-500 font-mono">{formData.colors[option.key]}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Business Information - Default Profile */}
            {activeProfile === 'default' && (
              <div>
                <div className="flex items-center mb-6">
                  <SafeIcon icon={FiBuilding} className="w-6 h-6 text-gray-400 mr-3" />
                  <h2 className="text-xl font-semibold text-gray-900">Default Business Information</h2>
                </div>

                {/* Logo Upload */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Logo
                  </label>
                  <div className="flex items-start space-x-4">
                    {logoPreview ? (
                      <div className="relative w-32 h-32 border border-gray-300 rounded-lg p-2 bg-white flex items-center justify-center">
                        <img
                          src={logoPreview}
                          alt="Company Logo"
                          className="max-w-full max-h-full object-contain"
                          style={{ aspectRatio: 'auto' }}
                        />
                        <button
                          type="button"
                          onClick={removeLogo}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <SafeIcon icon={FiX} className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                        <span className="text-xs text-gray-400 text-center">No logo</span>
                      </div>
                    )}
                    <div className="flex-1">
                      <label className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                        <SafeIcon icon={FiUpload} className="w-4 h-4 mr-2 text-gray-500" />
                        <span className="text-sm text-gray-700">Choose file</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          className="hidden"
                        />
                      </label>
                      <p className="text-xs text-gray-500 mt-1">
                        PNG, JPG up to 2MB. Recommended: 200x200px
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company Name
                    </label>
                    <input
                      type="text"
                      value={formData.companyInfo.name}
                      onChange={(e) => handleCompanyInfoChange('name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Your Company Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.companyInfo.email}
                      onChange={(e) => handleCompanyInfoChange('email', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="company@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.companyInfo.phone}
                      onChange={(e) => handleCompanyInfoChange('phone', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tax ID
                    </label>
                    <input
                      type="text"
                      value={formData.companyInfo.taxId}
                      onChange={(e) => handleCompanyInfoChange('taxId', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Tax ID Number"
                    />
                  </div>
                </div>
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address
                  </label>
                  <textarea
                    value={formData.companyInfo.address}
                    onChange={(e) => handleCompanyInfoChange('address', e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Your company address"
                  />
                </div>
              </div>
            )}

            {/* Travel Agency Information */}
            {activeProfile === 'travel' && (
              <div>
                <div className="flex items-center mb-6">
                  <SafeIcon icon={FiGlobe} className="w-6 h-6 text-gray-400 mr-3" />
                  <h2 className="text-xl font-semibold text-gray-900">Travel Agency Information</h2>
                </div>

                {/* Travel Agency Logo Upload */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Travel Agency Logo
                  </label>
                  <div className="flex items-start space-x-4">
                    {travelLogoPreview ? (
                      <div className="relative w-32 h-32 border border-gray-300 rounded-lg p-2 bg-white flex items-center justify-center">
                        <img
                          src={travelLogoPreview}
                          alt="Travel Agency Logo"
                          className="max-w-full max-h-full object-contain"
                          style={{ aspectRatio: 'auto' }}
                        />
                        <button
                          type="button"
                          onClick={removeTravelLogo}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <SafeIcon icon={FiX} className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                        <span className="text-xs text-gray-400 text-center">No logo</span>
                      </div>
                    )}
                    <div className="flex-1">
                      <label className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                        <SafeIcon icon={FiUpload} className="w-4 h-4 mr-2 text-gray-500" />
                        <span className="text-sm text-gray-700">Choose file</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleTravelLogoUpload}
                          ref={travelLogoInputRef}
                          className="hidden"
                        />
                      </label>
                      <p className="text-xs text-gray-500 mt-1">
                        PNG, JPG up to 2MB. Recommended: 200x200px
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Business Trade Name
                    </label>
                    <input
                      type="text"
                      value={formData.travelAgencyInfo.name}
                      onChange={(e) => handleTravelAgencyInfoChange('name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Your Travel Agency Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.travelAgencyInfo.email}
                      onChange={(e) => handleTravelAgencyInfoChange('email', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="travel@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.travelAgencyInfo.phone}
                      onChange={(e) => handleTravelAgencyInfoChange('phone', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Save Button */}
            <div className="flex justify-end pt-6 border-t border-gray-200">
              <motion.button
                type="submit"
                className="flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <SafeIcon icon={FiSave} className="w-5 h-5 mr-2" />
                Save Settings
              </motion.button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default Settings;